import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { exclusiveToInclusive } from "utils/salebillRate";

const useBillStore = create(
  devtools(
    (set, get) => ({
      // Edit mode tracking
      editBillId: null,
      savedBillDiscounts: [], // Before-tax discounts saved in the bill (for merge during edit)
      savedAfterTaxDiscounts: [], // After-tax discounts saved in the bill (for merge during edit)
      savedAdditionalAmounts: null, // Saved freight, TCS for edit mode initialization

      // Bill header state - matches saleBillDetails from SetSaleBillTop.jsx
      billHeader: {
        salebillno: "",
        salebilldate: "",
        orddespatch: "",
        orderedby: "",
        lrno: "",
        lr_date: null,
        salebillduedate: null,
        billtype: null,
        cusid: "",
        vendid: "",
        tptid: "",
        godown: 0,
        totalqtypcs: 0,
        remark: "",
        pldate: null, // Price list date
        is_scheme: 0, // 0 = No, 1 = Yes
        billdefault: 1, // 1 = Default (with items), 0 = Without items
        cndntype: "", // CN/DN type ID
        grossamt: 0, // Manual gross for CNDN Direct(Without Item)
        settlementMode: "CREDIT",
        cashCustomerName: "",
        cashCustomer: {},
        currency_code: "INR",
        fx_rate_to_inr: 1,
        fx_rate_date: null,
        fx_rate_source: "",
      },

      // Document type - separate from billHeader
      docType: "", // "PL" or "AGENTBILL"

      // Select boxes state for dropdowns
      selectBoxes: {
        customer: null,           // selectedCustAccount
        vendor: null,             // selectedVendAccount  
        transporter: null,        // selectedTranportAccount
        pricelist: null,          // selectedPricelist
        billType: null,           // selectedBillType
        order: null,              // selectedOrder (for ORD type)
        packingList: null,        // selectedPackingList (for PL type)
        transferCustomer: null,   // For PL case diversion - customer to transfer to
        cndnType: null,           // Selected CN/DN type (for CNDN docType)
      },

      // Table data state
      tableData: [],
      showTables: false,

      // Case diversion flag (for PL bills)
      confirmpltransfer: false,

      // Modal states
      showCaseDiversionModal: false,

      // Filters and query state
      pricelistFilters: { supid: "" },
      queryPricelist: "",

      // Before tax discounts state (from profiling master or manual)
      // Each discount object can have: 
      // { id, discountId, order, shortname, name, percentage, amount, isPercentage }
      beforeTaxDiscounts: [],

      // After tax discounts state (C type from profiling master)
      afterTaxDiscounts: [],

      // Agent commission state
      agentCommission: {
        commType: "SBT", // "SBT" = Sale bill amount before Tax, "SB" = Sale bill amount
        percentage: 0,
        amount: 0,
      },

      // Additional sale bill fields
      billDefault: 1, // 1 = Default (with items), 0 = Without items
      isScheme: 0, // 0 = No, 1 = Yes
      freightAfterTax: 0,
      roundoff: 0,
      tcsAmount: 0,
      tcsPercentage: 0,
      
      // Taxes state
      taxes: {
        cgst: 0,
        sgst: 0,
        igst: 0,
      },

      // Selected packing lists/orders for bill creation
      selectedPackingLists: [], // Array of packing list IDs for PL bills
      selectedOrders: [], // Array of order IDs for ORD bills
      
      // Case details for lot quantities and case numbers
      caseDetails: [], // Array of { billcaseid, billcaseno, billlotqty }
      
      // Bill mode - for AGENTBILL
      billMode: { value: "default", label: "Default" }, // "default" or "without_items"

      // Action to set bill header
      setBillHeader: (headerData) =>
        set({ billHeader: headerData }, false, "set-bill-header"),

      // Action to update specific header fields
      updateBillHeader: (updates) =>
        set(
          (state) => ({
            billHeader: { ...state.billHeader, ...updates },
          }),
          false,
          "update-bill-header"
        ),

      // Reset bill header
      resetBillHeader: () =>
        set(
          {
            billHeader: {
              salebillno: "",
              salebilldate: "",
              orddespatch: "",
              orderedby: "",
              lrno: "",
              lr_date: null,
              salebillduedate: null,
              billtype: null,
              cusid: "",
              vendid: "",
              tptid: "",
              godown: 0,
              totalqtypcs: 0,
              remark: "",
              pldate: null,
              cndntype: "",
              grossamt: 0,
              settlementMode: "CREDIT",
              cashCustomerName: "",
              cashCustomer: {},
              currency_code: "INR",
              fx_rate_to_inr: 1,
              fx_rate_date: null,
              fx_rate_source: "",
            },
          },
          false,
          "reset-bill-header"
        ),

      // Action to set docType
      setDocType: (docType) =>
        set({ docType }, false, "set-doc-type"),

      // Utility function to check if header is complete
      isHeaderComplete: (docType) => {
        const { billHeader, selectBoxes } = get();
        const minimumCondition = !!(
          billHeader.salebillno.length &&
          billHeader.salebilldate &&
          selectBoxes.customer &&
          selectBoxes.vendor &&
          selectBoxes.billType
        );

        return (docType === "PL" || docType === "CNDN")
          ? minimumCondition
          : minimumCondition && selectBoxes.pricelist;
      },

      // Actions for select boxes
      updateSelectBox: (key, value) =>
        set(
          (state) => ({
            selectBoxes: { ...state.selectBoxes, [key]: value },
          }),
          false,
          `update-select-box-${key}`
        ),

      // Action to set all select boxes at once
      setSelectBoxes: (selectBoxes) =>
        set({ selectBoxes }, false, "set-select-boxes"),

      // Action to reset select boxes
      resetSelectBoxes: () =>
        set(
          {
            selectBoxes: {
              customer: null,
              vendor: null,
              transporter: null,
              pricelist: null,
              billType: null,
              order: null,
              packingList: null,
              cndnType: null,
            },
            beforeTaxDiscounts: [],
            afterTaxDiscounts: [],
            agentCommission: {
              commType: "SBT",
              percentage: 0,
              amount: 0,
            },
          },
          false,
          "reset-select-boxes"
        ),

      // Actions for pricelist filters
      setPricelistFilters: (filters) =>
        set({ pricelistFilters: filters }, false, "set-pricelist-filters"),

      // Actions for query pricelist
      setQueryPricelist: (query) =>
        set({ queryPricelist: query }, false, "set-query-pricelist"),

      // Actions for before tax discounts
      setBeforeTaxDiscounts: (discounts) =>
        set({ beforeTaxDiscounts: discounts }, false, "set-before-tax-discounts"),

      // Action to update specific discount in beforeTaxDiscounts
      updateBeforeTaxDiscount: (index, updates) =>
        set(
          (state) => ({
            beforeTaxDiscounts: state.beforeTaxDiscounts.map((discount, i) =>
              i === index ? { ...discount, ...updates } : discount
            ),
          }),
          false,
          "update-before-tax-discount"
        ),

      // Actions for after tax discounts
      setAfterTaxDiscounts: (discounts) =>
        set({ afterTaxDiscounts: discounts }, false, "set-after-tax-discounts"),

      updateAfterTaxDiscount: (index, updates) =>
        set(
          (state) => ({
            afterTaxDiscounts: state.afterTaxDiscounts.map((discount, i) =>
              i === index ? { ...discount, ...updates } : discount
            ),
          }),
          false,
          "update-after-tax-discount"
        ),

      // Actions for agent commission
      setAgentCommission: (commission) =>
        set({ agentCommission: commission }, false, "set-agent-commission"),

      updateAgentCommission: (updates) =>
        set(
          (state) => ({
            agentCommission: { ...state.agentCommission, ...updates },
          }),
          false,
          "update-agent-commission"
        ),

      // Actions for bill default and scheme
      setBillDefault: (billDefault) =>
        set({ billDefault }, false, "set-bill-default"),

      setIsScheme: (isScheme) =>
        set({ isScheme }, false, "set-is-scheme"),

      // Actions for PL case diversion
      setCaseDiversion: (transferCustomer) =>
        set(
          (state) => ({
            selectBoxes: {
              ...state.selectBoxes,
              transferCustomer: transferCustomer,
            },
            confirmpltransfer: true,
          }),
          false,
          "set-case-diversion"
        ),

      // Clear case diversion
      clearCaseDiversion: () =>
        set(
          (state) => ({
            selectBoxes: {
              ...state.selectBoxes,
              transferCustomer: null,
            },
            confirmpltransfer: false,
          }),
          false,
          "clear-case-diversion"
        ),

      // Actions for modal states
      setShowCaseDiversionModal: (show) =>
        set({ showCaseDiversionModal: show }, false, "set-show-case-diversion-modal"),

      resetCaseDiversionModal: () =>
        set(
          (state) => ({
            selectBoxes: {
              ...state.selectBoxes,
              transferCustomer: null,
            },
          }),
          false,
          "reset-case-diversion-modal"
        ),

      // Actions for freight, roundoff, TCS
      setFreightAfterTax: (freightAfterTax) =>
        set({ freightAfterTax }, false, "set-freight-after-tax"),

      setRoundoff: (roundoff) =>
        set({ roundoff }, false, "set-roundoff"),

      setTcsAmount: (tcsAmount) =>
        set({ tcsAmount }, false, "set-tcs-amount"),

      setTcsPercentage: (tcsPercentage) =>
        set({ tcsPercentage }, false, "set-tcs-percentage"),

      // Actions for taxes
      setTaxes: (taxes) =>
        set({ taxes }, false, "set-taxes"),

      updateTaxes: (updates) =>
        set(
          (state) => ({
            taxes: { ...state.taxes, ...updates },
          }),
          false,
          "update-taxes"
        ),

      // Actions for selected packing lists
      setSelectedPackingLists: (packingLists) =>
        set({ selectedPackingLists: packingLists }, false, "set-selected-packing-lists"),

      addPackingList: (packingListId) =>
        set(
          (state) => ({
            selectedPackingLists: [...state.selectedPackingLists, packingListId],
          }),
          false,
          "add-packing-list"
        ),

      removePackingList: (packingListId) =>
        set(
          (state) => ({
            selectedPackingLists: state.selectedPackingLists.filter(id => id !== packingListId),
          }),
          false,
          "remove-packing-list"
        ),

      // Actions for selected orders
      setSelectedOrders: (orders) =>
        set({ selectedOrders: orders }, false, "set-selected-orders"),

      addOrder: (orderId) =>
        set(
          (state) => ({
            selectedOrders: [...state.selectedOrders, orderId],
          }),
          false,
          "add-order"
        ),

      removeOrder: (orderId) =>
        set(
          (state) => ({
            selectedOrders: state.selectedOrders.filter(id => id !== orderId),
          }),
          false,
          "remove-order"
        ),

      // Actions for case details
      setCaseDetails: (caseDetails) =>
        set({ caseDetails }, false, "set-case-details"),

      addCaseDetail: (caseDetail) =>
        set(
          (state) => ({
            caseDetails: [...state.caseDetails, caseDetail],
          }),
          false,
          "add-case-detail"
        ),

      updateCaseDetail: (index, updates) =>
        set(
          (state) => ({
            caseDetails: state.caseDetails.map((detail, i) =>
              i === index ? { ...detail, ...updates } : detail
            ),
          }),
          false,
          "update-case-detail"
        ),

      removeCaseDetail: (index) =>
        set(
          (state) => ({
            caseDetails: state.caseDetails.filter((_, i) => i !== index),
          }),
          false,
          "remove-case-detail"
        ),

      // Action for bill mode
      setBillMode: (billMode) =>
        set({ billMode }, false, "set-bill-mode"),

      // Actions for table data
      setTableData: (tableData) =>
        set({ tableData }, false, "set-table-data"),

      // Actions for show tables
      setShowTables: (showTables) =>
        set({ showTables }, false, "set-show-tables"),

      // Action to add items to table data
      addItemsToTable: (items) =>
        set(
          (state) => ({
            tableData: [...state.tableData, ...items],
          }),
          false,
          "add-items-to-table"
        ),

      // Action to remove item from table data by index
      removeTableItem: (index) =>
        set(
          (state) => ({
            tableData: state.tableData.filter((_, i) => i !== index),
          }),
          false,
          "remove-table-item"
        ),

      // Action to update table item
      updateTableItem: (index, updates) =>
        set(
          (state) => ({
            tableData: state.tableData.map((item, i) =>
              i === index ? { ...item, ...updates } : item
            ),
          }),
          false,
          "update-table-item"
        ),

      // Full store reset
      resetStore: () =>
        set(
          {
            editBillId: null,
            savedBillDiscounts: [],
            savedAfterTaxDiscounts: [],
            savedAdditionalAmounts: null,
            billHeader: {
              salebillno: "",
              salebilldate: "",
              orddespatch: "",
              orderedby: "",
              lrno: "",
              lr_date: null,
              salebillduedate: null,
              billtype: null,
              cusid: "",
              vendid: "",
              tptid: "",
              godown: 0,
              totalqtypcs: 0,
              remark: "",
              pldate: null,
              is_scheme: 0,
              billdefault: 1,
              cndntype: "",
              grossamt: 0,
              settlementMode: "CREDIT",
              cashCustomerName: "",
              cashCustomer: {},
              currency_code: "INR",
              fx_rate_to_inr: 1,
              fx_rate_date: null,
              fx_rate_source: "",
            },
            docType: "",
            selectBoxes: {
              customer: null,
              vendor: null,
              transporter: null,
              pricelist: null,
              billType: null,
              order: null,
              packingList: null,
              transferCustomer: null,
              cndnType: null,
            },
            tableData: [],
            showTables: false,
            confirmpltransfer: false,
            showCaseDiversionModal: false,
            pricelistFilters: { supid: "" },
            queryPricelist: "",
            beforeTaxDiscounts: [],
            afterTaxDiscounts: [],
            agentCommission: {
              commType: "SBT",
              percentage: 0,
              amount: 0,
            },
            billDefault: 1,
            isScheme: 0,
            freightAfterTax: 0,
            roundoff: 0,
            tcsAmount: 0,
            tcsPercentage: 0,
            taxes: { cgst: 0, sgst: 0, igst: 0 },
            selectedPackingLists: [],
            selectedOrders: [],
            caseDetails: [],
            billMode: { value: "default", label: "Default" },
          },
          false,
          "reset-store"
        ),

      // Helper function to populate bill from existing data (for edit mode)
      populateBillFromData: (billData) => {
        const {
          salebillno,
          salebilldate,
          orddespatch,
          orderedby,
          lrno,
          lr_date,
          salebillduedate,
          salebillduedt,
          billtype,
          customer,
          vendor,
          transport,
          godown,
          pldate,
          remark,
          totalqtypcs,
          is_scheme,
          billdefault,
          bill_details,
          discounts_array,
          agentcomm,
          agentcommperc,
          commcond,
          freightaftax,
          tcsamt,
          tcsperc,
          cndntype,
        } = billData;

        const isCndn = billtype === "VCN" || billtype === "VDN";

        const tableData = (bill_details || []).map((detail) => {
          const itemDetail = detail.salebill_itemdetail;
          const itemMast = itemDetail?.agitemmast;

          return {
            selected: true,
            ordno: detail.ordno || "",
            caseno: detail.caseno || "",
            slotqty: detail.slotqty || 1,
            sqty: parseFloat(detail.sqty) || 0,
            srate: billData.currency_code !== "INR"
              ? parseFloat(detail.srate_fc) || 0
              : parseFloat(detail.srate) || 0,
            srate_fc: parseFloat(detail.srate_fc) || 0,
            srate_incl:
              billData.currency_code !== "INR"
                ? parseFloat(detail.srate_fc) || 0
                : detail.srate_incl !== undefined &&
              detail.srate_incl !== null &&
              detail.srate_incl !== ""
                ? parseFloat(detail.srate_incl) || 0
                : exclusiveToInclusive(detail.srate, detail.gstrt),
            dis1: isCndn ? 0 : (billData.currency_code !== "INR"
              ? parseFloat(detail.itmdis1amt_fc) || 0
              : parseFloat(detail.itmdis1amt) || 0),
            itmdis1amt_fc: parseFloat(detail.itmdis1amt_fc) || 0,
            dis2perc: isCndn ? 0 : parseFloat(detail.itmdis2rt) || 0,
            dis2: isCndn ? 0 : (billData.currency_code !== "INR"
              ? parseFloat(detail.itmdis2amt_fc) || 0
              : parseFloat(detail.itmdis2amt) || 0),
            itmdis2amt_fc: parseFloat(detail.itmdis2amt_fc) || 0,
            amt1_fc: parseFloat(detail.amt1_fc) || 0,
            othdis_fc: parseFloat(detail.othdis_fc) || 0,
            itmamt_fc: parseFloat(detail.itmamt_fc) || 0,
            tax: parseFloat(detail.gstrt) || 0,
            packingno: detail.packingno || "",
            slbno: "", // bill ID reference (empty for loaded data)
            billNoDisplay: isCndn ? (detail.ordno || "") : "",
            item: {
              itemid: detail.itemid,
              item_full_name: detail.item_full_name || "",
              itemName: itemMast?.itemName || "",
              itemCode: itemMast?.itemCode || "",
              brand: itemMast?.brand || "",
              subbrandname: itemMast?.subbrandname || "",
              itemuom: itemMast?.itemuom || "",
              itmhsnId: detail.itmhsnid,
              aguomconval: itemMast?.aguomconval || 1,
              shp: itemDetail?.shp || "",
              clt: itemDetail?.clt || "",
              sz: itemDetail?.sz || "",
              szuom: itemDetail?.szuom || "",
            },
            source: billtype || "",
            isAdditionalItems: false,
            itemDetails: [],
          };
        });

        // Build saved discounts from discounts_array, split by discount_type
        const allSavedDiscounts = (discounts_array || []).map((d) => ({
          id: d.id,
          discountId: d.discountId,
          order: d.order,
          name: d.name || "",
          shortname: d.shortname || "",
          type: d.type || "",
          discount_type: d.discount_type || "B",
          percentage: parseFloat(d.percentage) || 0,
          amount: billData.currency_code !== "INR"
            ? parseFloat(d.amount_fc) || 0
            : parseFloat(d.amount) || 0,
          discount_amt_fc: parseFloat(d.amount_fc) || 0,
          is_addition: d.is_addition || 0,
        }));
        const savedDiscounts = allSavedDiscounts.filter((d) => d.discount_type === "B");
        const savedAfterTaxDiscounts = allSavedDiscounts.filter((d) => d.discount_type === "C");

        const stateUpdate = {
          billHeader: {
            salebillno: salebillno || "",
            salebilldate: salebilldate || "",
            orddespatch: orddespatch || "",
            orderedby: orderedby || "",
            lrno: lrno || "",
            lr_date: lr_date || null,
            salebillduedate: salebillduedate || salebillduedt || null,
            billtype: billtype || null,
            cusid: customer?.ps_Id || "",
            vendid: vendor?.ps_Id || "",
            tptid: transport?.ps_Id || "",
            godown: Number(godown) || 0,
            totalqtypcs: totalqtypcs || 0,
            remark: remark || "",
            pldate: pldate || null,
            is_scheme: is_scheme || 0,
            billdefault: billdefault || 1,
            cndntype: cndntype || "",
            grossamt: billData.currency_code !== "INR"
              ? parseFloat(billData.grossamt_fc) || 0
              : parseFloat(billData.grossamt) || 0,
            currency_code: billData.currency_code || "INR",
            fx_rate_to_inr: parseFloat(billData.fx_rate_to_inr) || 0,
            fx_rate_date: billData.fx_rate_date || null,
            fx_rate_source: billData.fx_rate_source || "",
            adj: parseFloat(billData.adj) || 0,
            settlementMode:
              billData.cusid === "CASHAC" || billData.cashcusno === "CASHAC"
                ? "CASH"
                : "CREDIT",
            cashCustomerName: billData.cashCustomer?.cname || "",
            cashCustomer: billData.cashCustomer || {},
          },
          selectBoxes: {
            customer: customer ? {
              ...customer,
              stateid: customer.partySupDetail?.stateid || customer.stateid || "",
              ps_country: customer.partySupDetail?.ps_country || customer.ps_country || "IN",
            } : null,
            vendor: vendor ? {
              ...vendor,
              stateid: vendor.partySupDetail?.stateid || vendor.stateid || "",
            } : null,
            transporter: transport ? { ...transport } : null,
            pricelist: null,
            billType: billtype ? { value: billtype, label: getBillTypeLabel(billtype) } : null,
            order: null,
            packingList: null,
            transferCustomer: null,
            cndnType: null,
          },
          agentCommission: {
            commType: commcond || "SBT",
            percentage: parseFloat(agentcommperc) || 0,
            amount: parseFloat(agentcomm) || 0,
          },
        };

        // Set table data if we have bill details
        if (tableData.length > 0) {
          stateUpdate.tableData = tableData;
          stateUpdate.showTables = true;
        }

        // Store saved discounts for merge logic and set them as initial discounts
        if (savedDiscounts.length > 0) {
          stateUpdate.beforeTaxDiscounts = savedDiscounts;
          stateUpdate.savedBillDiscounts = savedDiscounts;
        }
        if (savedAfterTaxDiscounts.length > 0) {
          stateUpdate.afterTaxDiscounts = savedAfterTaxDiscounts;
          stateUpdate.savedAfterTaxDiscounts = savedAfterTaxDiscounts;
        }

        // Store additional amounts for edit mode initialization (freight, TCS)
        stateUpdate.savedAdditionalAmounts = {
          freightAfterTax: parseFloat(freightaftax) || 0,
          tcsAmount: parseFloat(tcsamt) || 0,
          tcsPercentage: parseFloat(tcsperc) || 0,
        };

        // Track that we're in edit mode
        stateUpdate.editBillId = billData.salebillid || null;

        set(stateUpdate, false, "populate-bill-from-data");
      },
    }),
    {
      name: "bill-store", // Name for devtools
    }
  )
);

// Helper function to get bill type label
const getBillTypeLabel = (value) => {
  const options = {
    "PL": "Packing List",
    "ORD": "Order",
    "DIR": "Direct",
    "STK": "Stock",
    "VCN": "Credit Note",
    "VDN": "Debit Note",
  };
  return options[value] || value;
};

export default useBillStore;
